import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  min-width: 0;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SubTitleText = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
`;

export const HeaderActionsRight = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

export const FinancierLink = styled(Link)`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.success : '#15803d')};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  width: 100%;
  min-width: 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const KpiBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const KpiLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const KpiSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const KpiValue = styled.div`
  font-size: 20px;
  font-weight: 800;
`;

export const KpiWarningValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.warning : '#b45309')};
`;

export const KpiSuccessValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.success : '#15803d')};
`;

export const KpiSuccessStrong = styled.strong`
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.success : '#15803d')};
`;

export const TableWrap = styled.div`
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  -webkit-overflow-scrolling: touch;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 11px 13px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

export const RightAlignTh = styled.th<{ $minWidth?: number }>`
  text-align: right;
  ${({ $minWidth }) => ($minWidth ? `min-width: ${$minWidth}px;` : '')}
`;

export const StickyRecipientTh = styled.th`
  position: sticky;
  left: 0;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  z-index: 3;
  box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);
`;

export const StickyTh = styled.th`
  position: sticky;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 2;
  box-shadow: -4px 0 8px rgba(0, 0, 0, 0.08);
  text-align: center;
`;

export const StickyRecipientTd = styled.td`
  position: sticky;
  left: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 2;
  box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);
`;

export const StickyTd = styled.td`
  position: sticky;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 2;
  box-shadow: -4px 0 8px rgba(0, 0, 0, 0.08);
  text-align: center;
`;

export const RightAlignTd = styled.td`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

export const RequestNumberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const RequestNumberText = styled.div`
  font-weight: 700;
`;

export const DemoBadge = styled(Badge)`
  font-size: 10px;
`;

export const CarryoverBadgeText = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#38bdf8' : '#0369a1')};
`;

export const MutedDateText = styled.div`
  font-size: 11px;
  opacity: 0.85;
`;

export const CounterpartyText = styled.div`
  font-weight: 700;
`;

export const CategoryText = styled.div`
  font-size: 11px;
  opacity: 0.85;
`;

export const PurposeTd = styled.td`
  max-width: 320px;
`;

export const InitiatorText = styled.div`
  font-size: 11px;
  opacity: 0.85;
  margin-top: 2px;
`;

export const AmountColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-end;
`;

export const RequestedText = styled.div`
  font-size: 11px;
  opacity: 0.85;
`;

export const InlineInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const InlineInputRowEnd = styled(InlineInputRow)`
  justify-content: flex-end;
`;

export const SmallInput = styled.input`
  width: 90px;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  font-weight: 700;
  text-align: right;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

export const MiniBtn = styled.button`
  padding: 2px 5px;
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #10b981;
    color: #10b981;
  }
`;

export const CarryoverBadge = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.warning : '#9a3412')};
  background: #f59e0b18;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #f59e0b44;
  margin-top: 2px;
  display: inline-block;
`;

export const ApprovedAmountText = styled.div`
  font-weight: 800;
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.success : '#15803d')};
`;

export const PaidText = styled.div`
  font-size: 11px;
  opacity: 0.8;
`;

export const RemainingBalanceText = styled.div`
  font-size: 10px;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.warning : '#b45309')};
`;

export const BankDocText = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.success : '#15803d')};
`;

export const ExecutedText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.success : '#15803d')};
`;

export const DecisionActionsRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const FormBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FormTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 10px;
  width: 100%;
  min-width: 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const Input = styled.input`
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

export const Select = styled.select`
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

export const FormErrorText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

export const ErrorAlert = styled.div`
  padding: 10px 16px;
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid #ef4444;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
  font-size: 13px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
